<!-- ZayaAI UI-5: shared windowed pager. Windowed page numbers (1 2 3 … N), first/prev/next/
     last arrows, and a "Page N" click-to-type jump. 1-based `current` (b-pagination compatible).
     Drop-in for b-pagination: :total / :per-page / :current.sync. See specs/2026-06-02-zayaai-ui-viewer/. -->
<template>
<div class="zaya-pager" v-if="totalPages > 1">
  <button class="pg-arrow" :disabled="current <= 1" @click="go(1)" aria-label="first">&laquo;</button>
  <button class="pg-arrow" :disabled="current <= 1" @click="go(current - 1)" aria-label="previous">&lsaquo;</button>

  <template v-for="(p, i) in windowed">
    <span v-if="p === '...'" :key="`e-${i}`" class="pg-ellipsis">&hellip;</span>
    <button v-else :key="p" class="pg-num" :class="{active: p === current}" @click="go(p)">{{ p }}</button>
  </template>

  <button class="pg-arrow" :disabled="current >= totalPages" @click="go(current + 1)" aria-label="next">&rsaquo;</button>
  <button class="pg-arrow" :disabled="current >= totalPages" @click="go(totalPages)" aria-label="last">&raquo;</button>

  <span class="pg-jump">
    <button v-if="!editing" class="pg-page" @click="startEdit">
      {{ $t('page') }} {{ current }} <i class="fas fa-pen"></i>
    </button>
    <input
      v-else
      ref="jump"
      class="pg-input"
      type="number"
      :min="1"
      :max="totalPages"
      v-model.number="jumpValue"
      @keydown.enter="commitJump"
      @keydown.esc="editing = false"
      @blur="editing = false"
    >
  </span>
</div>
</template>

<script>
export default {
  name: 'zaya-pager',
  props: {
    total: {type: Number, default: 0},
    perPage: {type: Number, default: 10},
    current: {type: Number, default: 1}
  },
  data() {
    return {editing: false, jumpValue: 1};
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / Math.max(1, this.perPage)));
    },
    windowed() {
      const total = this.totalPages;
      const cur = this.current;
      const delta = 2;
      const range = [];
      const left = Math.max(2, cur - delta);
      const right = Math.min(total - 1, cur + delta);
      range.push(1);
      if (left > 2) {
        range.push('...');
      }
      for (let i = left; i <= right; i += 1) {
        range.push(i);
      }
      if (right < total - 1) {
        range.push('...');
      }
      if (total > 1) {
        range.push(total);
      }
      return range;
    }
  },
  methods: {
    go(page) {
      const target = Math.min(this.totalPages, Math.max(1, page));
      if (target !== this.current) {
        this.$emit('update:current', target);
      }
    },
    startEdit() {
      this.jumpValue = this.current;
      this.editing = true;
      this.$nextTick(() => this.$refs.jump && this.$refs.jump.focus());
    },
    commitJump() {
      const target = parseInt(this.jumpValue, 10);
      if (!isNaN(target)) {
        this.go(target);
      }
      this.editing = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/colors.scss';

.zaya-pager {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.pg-arrow,
.pg-num,
.pg-page {
  min-width: 28px;
  height: 28px;
  padding: 0 7px;
  border: 1px solid $line-1;
  border-radius: 6px;
  background: #fff;
  color: $ink-3;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) { border-color: $primary; color: $primary; }
  &:disabled { opacity: 0.4; cursor: default; }
}

.pg-num.active {
  background: $primary;
  border-color: $primary;
  color: #fff;
}

.pg-ellipsis {
  padding: 0 2px;
  color: #8a91a1;
}

.pg-jump { margin-left: 8px; }

.pg-page {
  gap: 6px;
  color: $ink-3;
  .fas { font-size: 0.65rem; color: #8a91a1; }
}

.pg-input {
  width: 64px;
  height: 28px;
  border: 1px solid $primary;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 0.8rem;
  outline: none;
}
</style>
